import { type Event } from '@kalos/kalos-rpc';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import {
  useGetDefaultSearchFormValues,
  useSearchParamsForm,
} from '../../../../hooks/useFormWithSearchParams';
import { type LoadEventsByFilter } from '../../../../tools/helpers';
import { type ZodObjectWithModelKeys } from '../../../../tools/typeguargs';

type ServiceCallsSearchFields =
  | Pick<NonNullable<Event['customer']>, 'firstname' | 'lastname' | 'businessname'>
  | Pick<NonNullable<Event['property']>, 'address' | 'city' | 'zip'>
  | Pick<
      Event,
      | 'name'
      | 'logNotes'
      | 'logTechnicianAssigned'
      | 'logJobNumber'
      | 'logPo'
      | 'isResidential'
      | 'jobTypeId'
      | 'jobSubtypeId'
      | 'logJobStatus'
      | 'departmentId'
      | 'description'
    >
  | (Pick<LoadEventsByFilter['filter'], 'dateStartedFrom' | 'dateStartedTo'> & {
      usePropertyLevelSearch: boolean;
    });

const datePreprocessor = (val: unknown): Date | undefined => {
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    // Remove quotes if present and decode URI components
    const cleanVal = val.replace(/"/g, '').replace(/%3A/g, ':');
    const date = new Date(cleanVal);
    return isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
};

export const serviceCallsSearchScheme = z.object({
  address: z.string().optional(),
  subdivision: z.string().optional(),
  city: z.string().optional(),
  zip: z.string().optional(),
  businessname: z.string().optional(),
  firstname: z.string().optional(),
  lastname: z.string().optional(),
  name: z.string().optional(),
  logNotes: z.string().optional(),
  logTechnicianAssigned: z.number().optional(),
  logJobNumber: z.string().optional(),
  logPo: z.string().optional(),
  // only 0,1,2
  isResidential: z.number().optional(),
  jobTypeId: z.number().optional(),
  jobSubtypeId: z.number().optional(),
  logJobStatus: z.string().optional(),
  dateStartedFrom: z.preprocess(datePreprocessor, z.date().optional()),
  dateStartedTo: z.preprocess(datePreprocessor, z.date().optional()),
  departmentId: z.number().optional(),
  description: z.string().optional(),
  usePropertyLevelSearch: z.boolean().optional(),
}) satisfies ZodObjectWithModelKeys<ServiceCallsSearchFields>;

export type ServiceCallsSearchScheme = z.infer<typeof serviceCallsSearchScheme>;
export const defaultServiceCallsSearchValues: ServiceCallsSearchScheme = {
  address: '',
  subdivision: '',
  city: '',
  zip: '',
  businessname: '',
  firstname: '',
  lastname: '',
  name: '',
  logNotes: '',
  logTechnicianAssigned: undefined,
  logJobNumber: '',
  logPo: '',
  isResidential: 0,
  jobTypeId: 0,
  jobSubtypeId: 0,
  logJobStatus: '',
  dateStartedFrom: undefined,
  dateStartedTo: undefined,
  departmentId: 0,
  usePropertyLevelSearch: true,
  description: '',
};

export const useServiceCallsSearchForm = ({ isSelector }: { isSelector: boolean }) => {
  return useSearchParamsForm({
    schema: serviceCallsSearchScheme,
    defaultValues: useGetDefaultSearchFormValues(
      serviceCallsSearchScheme,
      defaultServiceCallsSearchValues,
    ),
    disableParamsSync: isSelector,
    defaultResetValues: defaultServiceCallsSearchValues,
  });
};

export const useServiceCallsSearchFormContext = () => {
  return useFormContext<ServiceCallsSearchScheme>();
};
